/**
 * Select Product Variant Action
 * Triggered by: arc:action:selectVariant
 */

export const runSelectVariant = async (data = {}) => {
    try {
        const { 
            variantElement, 
            variantId, 
            variantPrice, 
            variantAvailable, 
            variantImage, 
            variantTitle, 
            variantMetafields,
            autoUpdate = true 
        } = data;

        if (!variantElement && !variantId) {
            throw new Error('Either variantElement or variantId is required');
        }

        let targetElement = variantElement;
        
        // If only variantId is provided, find the element
        if (!targetElement && variantId) {
            targetElement = document.querySelector(`input[data-variant-id="${variantId}"]`);
            if (!targetElement) {
                throw new Error(`Variant element not found for ID: ${variantId}`);
            }
        }

        console.log("ARCTHEME: Updating variant ID input", targetElement.value);

        // Remove selected class from current variant
        const currentSelected = document.querySelector('.variant-selects input[type="radio"].selected');
        if (currentSelected) {
            currentSelected.classList.remove("selected");
        }

        // Add selected class to new variant
        targetElement.classList.add("selected");

        // Store current variant globally
        window.currentVariant = targetElement.getAttribute("data-variant-id") || variantId;

        if (autoUpdate) {
            // Update all related elements
            await updateAvailability(targetElement);
            await updateProductsIncluded(targetElement);
            await updateAddtoCartInputIds(targetElement);
            await updateSelectedVariantTitle(targetElement);
            await switchVariantImage(targetElement);
        }

        // Dispatch variant switched event
        window.dispatchEvent(new CustomEvent("variantSwitched", {
            detail: {
                variant: targetElement,
                variantId: window.currentVariant,
                variantPrice: targetElement.getAttribute("data-variant-price"),
                variantAvailable: targetElement.getAttribute("data-variant-available"),
                variantImage: targetElement.getAttribute("data-variant-image"),
                variantTitle: targetElement.value
            }
        }));

        return {
            success: true,
            variantId: window.currentVariant,
            variantElement: targetElement,
            message: 'Variant selected successfully'
        };

    } catch (error) {
        console.error('Select Variant error:', error);
        
        window.dispatchEvent(new CustomEvent('variant:error', {
            detail: {
                error: error.message,
                action: 'selectVariant'
            }
        }));

        return {
            success: false,
            error: error.message
        };
    }
};

// Helper function to update availability
const updateAvailability = async (targetVariant) => {
    const variantAvailable = targetVariant.getAttribute("data-variant-available");
    const quantityInputEl = document.getElementById('quantityInput');
    const qty = quantityInputEl?.querySelector('input')?.value || 1;
    const variantPrice = targetVariant.getAttribute("data-variant-price");
    
    // Get currency symbol from Alpine store or fallback
    const currencySymbol = window.Alpine?.store('currency')?.getCurrencySymbol() || '$';
    const parsedPrice = parseFloat(variantPrice?.replace("$", ""));
    let displayPrice = `${currencySymbol}${(parsedPrice * qty).toFixed(2)}`;

    if (variantAvailable === "false") {
        if (quantityInputEl) quantityInputEl.classList.add('disabled');

        // Check if back in stock form should be shown
        const showBackInStockForm = document.querySelector('.pdp-details .stock-indicator');
        
        if (!showBackInStockForm) {
            // Dispatch event to update add button
            window.dispatchEvent(new CustomEvent('addButton:update', {
                detail: {
                    disabled: true,
                    text: 'Out of Stock',
                    modifyClass: true,
                    price: variantPrice
                }
            }));
        } else {
            // Show back in stock form
            document.querySelector('.pdp-details .add-group')?.classList.add('hidden');
            document.querySelector('.pdp-details .stock-indicator')?.classList.remove('hidden');
            
            window.dispatchEvent(new CustomEvent('addButton:hide', { detail: {} }));
            window.dispatchEvent(new CustomEvent('backInStock:show', { detail: {} }));
        }
    } else {
        if (quantityInputEl) quantityInputEl.classList.remove('disabled');
        document.querySelector('.pdp-details .stock-indicator')?.classList.add('hidden');
        document.querySelector('.pdp-details .add-group')?.classList.remove('hidden');

        // Check if price should be shown in add to cart
        const showPriceInAddToCart = document.querySelector('[data-show-price-in-addtocart]')?.dataset.showPriceInAddtocart === 'true';
        
        const buttonText = showPriceInAddToCart ? `Add to Cart | ${displayPrice}` : 'Add to Bag';
        
        window.dispatchEvent(new CustomEvent('addButton:update', {
            detail: {
                disabled: false,
                text: buttonText,
                modifyClass: true,
                price: parsedPrice
            }
        }));
    }
};

// Helper function to update products included
const updateProductsIncluded = async (targetVariant) => {
    const variantMetafields = targetVariant.getAttribute("data-variant-metafields");
    
    if (variantMetafields) {
        const metafieldIds = variantMetafields
            .split(",")
            .map((id) => id.trim())
            .filter((id) => id);
            
        window.dispatchEvent(new CustomEvent("variantProductsIncluded", {
            detail: {
                ids: metafieldIds,
            },
        }));
    }
};

// Helper function to update add to cart input IDs
const updateAddtoCartInputIds = async (targetVariant) => {
    const productForms = document.querySelectorAll('.pdp-details .product-form input[name="id"]');
    const variantId = targetVariant.getAttribute("data-variant-id");

    console.log('Updating product id, variant id:', variantId);
    
    productForms.forEach((productForm) => {
        productForm.value = variantId;
    });
};

// Helper function to update selected variant title
const updateSelectedVariantTitle = async (targetVariant) => {
    const newVariantTitle = targetVariant.value;
    const variantTitleElements = document.querySelectorAll("[data-arctheme-variant-title]");
    
    variantTitleElements.forEach((element) => {
        element.innerHTML = newVariantTitle;
    });
};

// Helper function to switch variant image
const switchVariantImage = async (targetVariant) => {
    const variantImage = targetVariant.getAttribute("data-variant-image");
    
    if (variantImage) {
        window.dispatchEvent(new CustomEvent("variantImageSwitch", {
            detail: {
                image: variantImage,
            }
        }));
    }
};

export default runSelectVariant;
